import { useCallback, useEffect, useState } from "react";
import { useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { juneTrack } from "../../../utils/june/analytics";

import InboxInput from "./InboxInput";
import { inboxActiveTab, InboxActiveTab } from "../../../types/inbox/enum";
import InboxToggleList from "./InboxList/InboxToggleList";
import { TaskListWrapper } from "./TaskListWrapper";
import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";

import useHandleJuneBlockData from "../../../hooks/june/useHandleJuneBlockdata";
import { inboxQueryKeys } from "../../../react-query/inbox/queries";
import { BlockType } from "../../../types/block/type";
import { InboxToggleTitle } from "../../../types/inbox/enum";
import { BasicBlock, ItemStatus } from "../../../types/block/enum";
import {
  useDeleteInboxBlockMutationOptions,
  useReorderInboxBlockMutationOptions,
} from "../../../react-query/inbox/mutations";
import { useUpdateInboxCheckboxMutation } from "../../../react-query/inbox/useUpdateInboxCheckboxMutation";
import { removeClientOnlyFields } from "../../../utils/common/block/removeClientOnlyFields";
import { blockQueryKey } from "../../../react-query/block/queries";
import { JUNE_EVENT, JUNE_LOCATION } from "../../../hooks/june/juneEvent";
import { useDuplicateInboxBlockMutation } from "../../../react-query/inbox/useDuplicateInboxBlockMutation";
import { toastState } from "../../../recoil/toast/toastState";
import { ToastKind } from "../../../types/toast";
import { getToastMessageByKind } from "../../../utils/toast/toastUtils";
import { debounceInvalidate } from "../../../services/optimistic-updates/debounceInvalidate";
import InboxGeneral from "./InboxList/InboxGeneral";
import TodayGeneral from "./InboxList/TodayGeneral";

import InboxToCalendarDragLayer from "./InboxToCalendarDragLayer";
import { useDeleteBlockMutation } from "../../../react-query/block/useDeleteBlockMutation";
import { isAxiosError } from "axios";
import { InboxAccordion } from "../../../types/inbox/type";
import { generateUUID } from "../../../utils/uuid";

interface TaskListProps {
  activeTab: InboxActiveTab;
  toggleVisibility: InboxAccordion;
  /** 백엔드가 보내는 block들과 Google Calendar 동기화 여부 */
  hasBlocksSynchronizedWithGoogle: boolean;
}

export default function TaskList({
  activeTab,
  toggleVisibility,
  hasBlocksSynchronizedWithGoogle,
}: TaskListProps) {
  const queryClient = useQueryClient();

  const [taskSelectedId, setTaskSelectedId] = useState<number | null>(null);
  const [taskDetail, setTaskDetail] = useRecoilState(taskPopupState);
  const resetTaskDetail = useResetRecoilState(taskPopupState);
  const setToast = useSetRecoilState(toastState);

  const handleJuneBlockData = useHandleJuneBlockData();

  /** mutations */
  const useReorderInboxBlock = useMutation(useReorderInboxBlockMutationOptions());
  const useDuplicateInboxBlock = useDuplicateInboxBlockMutation();
  const useDeleteInboxGeneralBlock = useDeleteBlockMutation();
  const useDeleteInboxDividerBlock = useMutation(useDeleteInboxBlockMutationOptions());
  const useUpdateInboxCheckbox = useUpdateInboxCheckboxMutation();

  useEffect(
    function resetTaskSelectedId() {
      // inbox에서 taskDetail을 연 상태에서 calendar의 taskDetail을 열면 inbox의 taskSelectedId 초기화
      const isCurrentInboxBlockOpen = taskDetail.data?.id === taskSelectedId;
      if (taskSelectedId && !isCurrentInboxBlockOpen) {
        handleSaveTaskSelectedId(null);
      }
    },
    [taskSelectedId, taskDetail.data]
  );

  // 해당 함수는 inbox general에만 적용
  const handleDataDelete = useCallback(
    async (data: BlockType) => {
      const updateData = {
        itemStatus: ItemStatus.DELETED,
        allDay: data.allDay,
        hangoutLink: data.hangoutLink,
        meetingCode: data.meetingCode,
        linkData: data.linkData,
        attendees: data.attendees,
        location: data.location,
        priority: data.priority,
      };

      useDeleteInboxGeneralBlock.mutate(
        { id: data.id, payload: updateData, prevData: data },
        {
          onSuccess: () => {
            juneTrack(JUNE_EVENT.DELETE_BLOCK, {
              location: activeTab === inboxActiveTab.INBOX ? "inbox" : "today_inbox",
              type: BasicBlock.TASK,
            });
            // 휴지통이 열려있는 상태에서 삭제 시 휴지통에 데이터 추가
            queryClient.invalidateQueries({
              queryKey: blockQueryKey.getAllByParams({ itemStatus: ItemStatus.DELETED }),
            });
          },
        }
      );
    },
    [activeTab, queryClient, useDeleteInboxGeneralBlock]
  );

  const onClickCheckBox = useCallback(
    (e: React.MouseEvent, block: BlockType) => {
      e.stopPropagation();
      const isDone = block.itemStatus === ItemStatus.COMPLETED;
      const newStatus = isDone ? ItemStatus.IN_PROGRESS : ItemStatus.COMPLETED;
      const updateData = {
        itemStatus: newStatus,
        allDay: block.allDay,
        hangoutLink: block.hangoutLink,
        meetingCode: block.meetingCode,
        linkData: block.linkData,
        attendees: block.attendees,
        location: block.location,
        priority: block.priority,
      };

      useUpdateInboxCheckbox.mutate({ id: block.id, payload: updateData, prevData: block });
      // NOTE INBOX_GENERAL의 경우만 <InboxBlock> / handleClickCheckbox 사용하고 있지 않아서 따로 처리

      juneTrack(
        newStatus === ItemStatus.COMPLETED ? JUNE_EVENT.DONE_TASK : JUNE_EVENT.UNDONE_TASK,
        {
          location: "inbox",
        }
      );
    },
    [useUpdateInboxCheckbox, juneTrack]
  );

  const handleDeleteDivider = useCallback(
    (selectedDividerId: number) => {
      useDeleteInboxDividerBlock.mutate(selectedDividerId, {
        onSuccess: () => {
          queryClient.setQueryData(inboxQueryKeys.inboxGeneral(), (oldData: BlockType[]) => {
            if (!oldData) return [];
            return oldData.filter((block) => block.id !== selectedDividerId);
          });
          debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all());
          juneTrack(JUNE_EVENT.DELETE_DIVIDER, { location: JUNE_LOCATION.INBOX });
        },
      });
    },
    [juneTrack]
  );

  const createNewBlockItem = (data: BlockType, isRecurringBlock: boolean, isDivider: boolean) => {
    if (isDivider) {
      return {
        title: "",
        blockType: BasicBlock.DIVIDER,
        visibility: null,
        transparency: null,
      };
    }
    return removeClientOnlyFields(data, isRecurringBlock);
  };

  const handleDuplicateInboxBlock = useCallback(
    (e: React.MouseEvent, data: BlockType) => {
      const isDivider = data.blockType === BasicBlock.DIVIDER;
      const isRecurringBlock = Boolean(data.recurrence?.length || data.originalId);

      const { originalId, originalStart, ...recurringDeletedBlock } = data;

      const newItem = createNewBlockItem(recurringDeletedBlock, isRecurringBlock, isDivider);
      const displayOrderItem = {
        ...newItem,
        clientId: generateUUID(),
        displayOrder: data.displayOrder,
      };

      useDuplicateInboxBlock.mutate(
        { payload: displayOrderItem, originalBlock: data },
        {
          onSuccess: () => {
            setToast({
              isVisible: true,
              type: ToastKind.SUCCESS,
              message: getToastMessageByKind({
                type: ToastKind.SUCCESS,
                action: "duplicate",
                target: "Task",
              }),
              triggerId: Date.now(),
            });

            juneTrack(isDivider ? JUNE_EVENT.DUPLICATE_DIVIDER : JUNE_EVENT.DUPLICATE_BLOCK, {
              location: activeTab === inboxActiveTab.INBOX ? "inbox" : "today_inbox",
              blockType: BasicBlock.TASK,
            });
          },
          onError: (err) => {
            if (isAxiosError(err)) {
              setToast({
                isVisible: true,
                type: ToastKind.ERROR,
                message: getToastMessageByKind({
                  type: ToastKind.ERROR,
                  action: "duplicate",
                }),
                triggerId: Date.now(),
              });
            }
          },
        }
      );
    },
    [useDuplicateInboxBlock, setToast, juneTrack, activeTab]
  );

  const handleSaveTaskSelectedId = useCallback((id: number | null) => {
    setTaskSelectedId(id);
  }, []);

  const handleReorder = useCallback(
    (ids: number[]) => {
      useReorderInboxBlock.mutate({ ids });
    },
    [useReorderInboxBlock]
  );

  const handleClickInboxBlock = useCallback(
    (e: React.MouseEvent, data: BlockType, ref: React.RefObject<HTMLDivElement>) => {
      if (taskSelectedId === data.id) {
        handleSaveTaskSelectedId(null);
        return resetTaskDetail();
      }

      const { clientX, clientY } = e;
      const rect = ref?.current?.getBoundingClientRect();
      const x = (rect?.right ?? 0) + 8;
      const y = rect?.top ?? 0;

      if (clientX !== 0 || clientY !== 0) {
        setTaskDetail({
          isVisible: true,
          data: data,

          targetRect: rect ? rect : null,
          onClose: () => {
            handleSaveTaskSelectedId(null);
          },
          handleDataDuplicate: handleDuplicateInboxBlock,
          type: activeTab === inboxActiveTab.INBOX ? "inbox" : "today_inbox",
        });
      }
      handleSaveTaskSelectedId(data.id);

      handleJuneBlockData({
        action: "view_block_detail",
        location: activeTab === inboxActiveTab.INBOX ? "inbox" : "today_inbox",
        blockType: BasicBlock.TASK,
      });
    },
    [taskSelectedId, handleSaveTaskSelectedId, handleDuplicateInboxBlock]
  );

  const flexGrow =
    activeTab === inboxActiveTab.INBOX
      ? toggleVisibility[inboxActiveTab.INBOX].overdue ||
        toggleVisibility[inboxActiveTab.INBOX].completed
        ? 0
        : 1
      : toggleVisibility[inboxActiveTab.TODAY].overdue ||
          toggleVisibility[inboxActiveTab.TODAY].completed
        ? 0
        : 1;

  return (
    <TaskListWrapper>
      <InboxInput
        activeTab={activeTab}
        handleSaveTaskSelectedId={handleSaveTaskSelectedId}
      />
      {activeTab === inboxActiveTab.INBOX && (
        <>
          <InboxGeneral
            taskSelectedId={taskSelectedId}
            handleSaveTaskSelectedId={handleSaveTaskSelectedId}
            handleDeleteDivider={handleDeleteDivider}
            handleDuplicateInboxBlock={handleDuplicateInboxBlock}
            handleReorder={handleReorder}
            handleClickInboxBlock={handleClickInboxBlock}
            handleDataDelete={handleDataDelete}
            onClickCheckBox={onClickCheckBox}
            flexGrow={flexGrow}
            hasBlocksSynchronizedWithGoogle={hasBlocksSynchronizedWithGoogle}
          />

          {toggleVisibility[inboxActiveTab.INBOX].overdue && (
            <InboxToggleList
              title={InboxToggleTitle.OVERDUE}
              activeTab={activeTab}
              onDataDuplicate={handleDuplicateInboxBlock}
              taskSelectedId={taskSelectedId}
              onSelect={handleSaveTaskSelectedId}
              onClickInboxBlock={handleClickInboxBlock}
              hasBlocksSynchronizedWithGoogle={hasBlocksSynchronizedWithGoogle}
            />
          )}
        </>
      )}

      {activeTab === inboxActiveTab.TODAY && (
        <>
          <TodayGeneral
            flexGrow={flexGrow}
            activeTab={activeTab}
            onDataDelete={handleDataDelete}
            onDataDuplicate={handleDuplicateInboxBlock}
            taskSelectedId={taskSelectedId}
            onSelect={handleSaveTaskSelectedId}
            onClickInboxBlock={handleClickInboxBlock}
          />
          {toggleVisibility[inboxActiveTab.TODAY].overdue && (
            <InboxToggleList
              title={InboxToggleTitle.OVERDUE}
              activeTab={activeTab}
              onDataDuplicate={handleDuplicateInboxBlock}
              taskSelectedId={taskSelectedId}
              onSelect={handleSaveTaskSelectedId}
              onClickInboxBlock={handleClickInboxBlock}
              hasBlocksSynchronizedWithGoogle={hasBlocksSynchronizedWithGoogle}
            />
          )}
        </>
      )}
      <InboxToCalendarDragLayer />
    </TaskListWrapper>
  );
}
